import { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid2 from '@mui/material/Unstable_Grid2';

import Eyebrow from '@/components/Eyebrow/Eyebrow';
import { DialogContent } from '@/components/FormResultDialog/FormResultDialog';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import Link from '@/components/Link/Link';
import {
  LeadForm,
  LeadFormStyle,
} from '@/components/TopLevelComponents/LeadForm/LeadForm';
import { GoogleUrls } from '@/constants/baseUrls';
import { Contentful, FrontifyImageContent, LinkContent } from '@/contentful';
import { LeadFormType } from '@/enums/FormType';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { ActionModalCF, LeadCreatedModalCF } from '@/types';
import { getLeadFormDialogContent } from '@/utils/leadForm';

import useStyles from './ActionUserInput.styles';
import BulletedList from './BulletedList';

export interface LeadModalContent extends Contentful {
  fields: {
    title: string;
    subtitle?: string;
    action?: string;
    modalImage: FrontifyImageContent[];
  };
}

export interface ActionUserInputContent extends Contentful {
  fields: {
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    bulletedList?: string[];
    formHeading?: string;
    ownerQuestion?: string;
    submitButtonText?: string;
    tcpa: RichTextDocument;
    footerText: string;
    footerLink?: LinkContent;
    leadCreatedModal?: LeadCreatedModalCF | ActionModalCF;
    leadNotInServiceModal?: LeadCreatedModalCF | ActionModalCF;
    campaignId?: string;
    googleSheetUrls?: GoogleUrls;
    formType?: LeadFormType;
    dialogContent?: DialogContent;
    overrideCampaignId?: boolean;
  };
}

export interface ActionUserInputProps {
  content: ActionUserInputContent;
  sectionPosition?: number;
  componentPosition?: number;
}

export default function ActionUserInput({
  content: {
    fields: {
      eyebrow,
      heading,
      subheading,
      bulletedList,
      formHeading,
      ownerQuestion,
      submitButtonText,
      tcpa,
      footerText,
      footerLink,
      leadCreatedModal,
      leadNotInServiceModal,
      campaignId,
      googleSheetUrls,
      formType,
      overrideCampaignId = false,
    },
  },
  sectionPosition,
  componentPosition,
}: ActionUserInputProps) {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes, cx } = useStyles();
  const { isSmall, isExtraSmall } = useViewPortSize();
  const isMdOrLarger = !isExtraSmall && !isSmall;
  const isFirstComponentOfPage =
    sectionPosition === 0 && componentPosition === 0;

  const successDialogContent = getLeadFormDialogContent(
    leadCreatedModal,
    isMdOrLarger
  );
  const failDialogContent = getLeadFormDialogContent(
    leadNotInServiceModal,
    isMdOrLarger
  );

  return (
    <div className={classes.container}>
      <GridContainer2 gridProps={{ className: classes.contentContainer }} fixed>
        {/* Text Content */}
        <Grid2 xs={12} md={5} className={classes.textContentContainer}>
          <div className={classes.textContent}>
            <>
              {eyebrow && <Eyebrow text={eyebrow} isOnDark={false} />}
              {heading && !isFirstComponentOfPage && (
                <h1 className={globalClasses.h1}>{heading}</h1>
              )}
              {heading && isFirstComponentOfPage && (
                <h2 className={globalClasses.h1}>{heading}</h2>
              )}
              {subheading && (
                <p className={cx(globalClasses.body2, classes.subheading)}>
                  {subheading}
                </p>
              )}
              {bulletedList && (
                <BulletedList
                  list={bulletedList}
                  className={classes.bulletedList}
                />
              )}
            </>
          </div>
        </Grid2>

        {isMdOrLarger && <Grid2 xs={1} />}

        {/* Lead Form */}
        <Grid2 xs={12} md={6} className={classes.leadForm}>
          <LeadForm
            heading={formHeading}
            ownerQuestion={ownerQuestion}
            submitButtonText={submitButtonText}
            tcpa={tcpa}
            successDialogContent={successDialogContent}
            failDialogContent={failDialogContent}
            campaignId={campaignId}
            googleSheetUrls={googleSheetUrls}
            formType={formType}
            overrideCampaignId={overrideCampaignId}
            leadFormstyle={LeadFormStyle.ACTION_USER_INPUT}
          />
        </Grid2>
      </GridContainer2>

      {/* Footer */}
      <GridContainer2>
        <Grid2 xs={12} className={classes.footer}>
          <p className={cx(globalClasses.h4, classes.footerText)}>
            {footerText}
          </p>
          {footerLink && (
            <Link
              href={footerLink.fields.url}
              className={cx(globalClasses.body2, classes.footerLink)}
            >
              {footerLink.fields.text}
            </Link>
          )}
        </Grid2>
      </GridContainer2>
    </div>
  );
}
